/**
 * Application_Form transport seam — public entry point + per-surface flag
 * selection (tasks 19.3, 26.4; mirrors `merit/client/index.ts`).
 *
 * Consumers import `applicationFormClient` from here and nothing else; they
 * never know whether they are talking to the mock or the live AppSync client.
 *
 * The rollout is SURFACE-BY-SURFACE (Req 23.8): the five Legacy_Consumer
 * surfaces — onboarding, builder, renderer, review, and the merit judge
 * projection — each own a disjoint slice of the `ApplicationFormClient` methods
 * and flip mock→live INDEPENDENTLY behind their own
 * `VITE_USE_MOCK_APPLICATION_FORM_<SURFACE>` flag. A surface is cut over the
 * moment its own integration tests pass, with no call-site changes. Every
 * surface defaults to the SAFE value (mock in dev; the live AppSync client in a
 * production build, where the per-competition opt-in on the backend keeps prod
 * competitions on the legacy path until a director publishes — Req 23.5, 23.7).
 *
 * The mock→live selection mirrors the `meritClient` rollout: the in-memory mock
 * is used only in a Vite dev build with that surface's mock flag on
 * (`import.meta.env.DEV && useMockApplicationFormSurface(surface)`); otherwise
 * the live AppSync client. `createPerSurfaceApplicationFormClient` is the
 * reusable selection factory so the wiring is testable in isolation
 * (task 19.4 / 26.5).
 */

import type { ApplicationFormClient } from './applicationFormClient';
import {
  APPLICATION_FORM_SURFACES,
  useMockApplicationFormSurface,
  type ApplicationFormSurface,
} from './applicationFormClient';
import { mockApplicationFormClient } from './mockApplicationFormClient';
import { appSyncApplicationFormClient } from './appSyncApplicationFormClient';

export type { ApplicationFormClient } from './applicationFormClient';
export {
  APPLICATION_FORM_SURFACES,
  USE_MOCK_APPLICATION_FORM,
  useMockApplicationFormSurface,
} from './applicationFormClient';
export type { ApplicationFormSurface } from './applicationFormClient';
export {
  createMockApplicationFormClient,
  mockApplicationFormClient,
} from './mockApplicationFormClient';
export { appSyncApplicationFormClient } from './appSyncApplicationFormClient';
export * as applicationFormOperations from './operations';
export * from './types';

/**
 * Which rollout surface owns each `ApplicationFormClient` method. The surfaces
 * partition the interface so a surface's mock→live flip moves exactly its own
 * methods and nothing else (Req 23.8). Keyed by every method name so the map is
 * exhaustive — adding a method to the interface forces a surface assignment here
 * (a compile error otherwise).
 */
export const APPLICATION_FORM_SURFACE_OF: Record<
  keyof ApplicationFormClient,
  ApplicationFormSurface
> = {
  // Onboarding + contact intake (applicant; Req 14, 15)
  getOnboardingDestination: 'onboarding',
  submitContactIntake: 'onboarding',

  // Builder + document catalog (director/admin; Req 1, 2, 3, 8, 20)
  getApplicationForm: 'builder',
  listFormPages: 'builder',
  createFormPage: 'builder',
  updateFormPage: 'builder',
  reorderFormPages: 'builder',
  deleteFormPage: 'builder',
  createFormField: 'builder',
  updateFormField: 'builder',
  reorderFormFields: 'builder',
  deleteFormField: 'builder',
  publishFormVersion: 'builder',
  getDocumentCatalog: 'builder',
  upsertDocumentCatalogEntry: 'builder',
  deleteDocumentCatalogEntry: 'builder',
  reorderDocumentCatalogEntries: 'builder',

  // Renderer (applicant; Req 5, 6, 7, 18, 19)
  getActiveFormVersion: 'renderer',
  getMyApplicationResponse: 'renderer',
  // Self-scoped rendered-response read for the applicant's own "Download My
  // Profile (PDF)" export — same self-scoping precedent as getMyPageApprovals.
  getMyRenderedResponse: 'renderer',
  // Contestant self-read of their own page approvals — powers the /apply
  // renderer's page lock + Approved pill (Req 21). Self-scoped, applicant surface.
  getMyPageApprovals: 'renderer',
  saveDraft: 'renderer',
  submit: 'renderer',
  selectDivision: 'renderer',
  selectSeason: 'renderer',
  saveTitlePreferences: 'renderer',
  // Season-scoped Title catalog read backing contestant title selection
  // (season-scoped-title-registry Req 5.1); pairs with saveTitlePreferences.
  listTitles: 'renderer',

  // Document uploads on the apply/renderer surface (applicant;
  // apply-document-uploads Req 2, 6, 7, 10)
  listMyUploadedDocuments: 'renderer',
  getDocumentUploadUrl: 'renderer',
  createDocumentReference: 'renderer',
  deleteDocumentReference: 'renderer',

  // Review + approvals (director; Req 9, 19, 21, 22.7)
  listResponses: 'review',
  // Read-only Approvals roll-up reads (approval-rollup-surfaces Req 2, 3); no
  // write path — the sole PageApproval writer stays the Discrete_Review path.
  listResponseValues: 'review',
  listPageApprovalsForPage: 'review',
  // One applicant's real per-page approval status/details for the Review
  // Detail (Req 21.2, 21.4, 21.7) — director-scoped, read-only.
  listPageApprovals: 'review',
  getResponseForReview: 'review',
  setApprovedTitle: 'review',
  reviewApplicationResponse: 'review',
  setPageApproval: 'review',
  getApprovalDetailVisibility: 'review',
  setApprovalDetailVisibility: 'review',
  // Director manual lock override, single + bulk (season-phase-deadlines Req 16, 18)
  setApplicationLockOverride: 'review',
  setApplicationLockOverridesForCompetition: 'review',

  // Merit judge projection (judge; Req 21.8, 22.4)
  getJudgeProjection: 'judgeProjection',
};

/**
 * Whether the in-memory mock should back a given surface's transport: true only
 * in a Vite dev build with that surface's mock flag on. Surfaces (and the
 * composition below) MUST use this — not `useMockApplicationFormSurface` alone —
 * to decide mock-vs-live, so a production build (where the flags are unset and
 * thus default to mock) still serves the live AppSync client. (This is the exact
 * `IS_MERIT_MOCK` lesson from the Merit seam: branching on the flag alone
 * ignores `import.meta.env.DEV` and strands deployed dev on mock data.)
 */
export function isApplicationFormSurfaceMock(surface: ApplicationFormSurface): boolean {
  return import.meta.env.DEV && useMockApplicationFormSurface(surface);
}

/**
 * Whether EVERY surface is currently on the mock transport. Retained for the
 * coarse "is the whole application-form seam mocked?" question; per-surface code
 * should prefer {@link isApplicationFormSurfaceMock}.
 */
export const IS_APPLICATION_FORM_MOCK: boolean =
  APPLICATION_FORM_SURFACES.every((surface) => isApplicationFormSurfaceMock(surface));

/**
 * The per-surface selection factory: assemble one `ApplicationFormClient` whose
 * each method is bound to the mock or the live implementation according to its
 * surface's DEV-gated flag (`isApplicationFormSurfaceMock`). Pure (no side
 * effects beyond reading the static env flags), so the selection logic is
 * testable in isolation (task 26.5).
 *
 * Because the selection is per method, surfaces flip independently: e.g. the
 * builder can run live while the renderer stays on the mock, exactly as a
 * staged rollout requires (Req 23.8).
 */
export function createPerSurfaceApplicationFormClient(
  mock: ApplicationFormClient,
  live: ApplicationFormClient,
): ApplicationFormClient {
  const methods = Object.keys(APPLICATION_FORM_SURFACE_OF) as (keyof ApplicationFormClient)[];
  const client = {} as Record<keyof ApplicationFormClient, unknown>;

  for (const method of methods) {
    const impl = isApplicationFormSurfaceMock(APPLICATION_FORM_SURFACE_OF[method]) ? mock : live;
    // Bind the chosen implementation's method so `this` stays correct and the
    // selection is resolved once at composition time.
    const fn = impl[method] as (...args: unknown[]) => unknown;
    client[method] = (...args: unknown[]) => fn.apply(impl, args);
  }

  return client as ApplicationFormClient;
}

/**
 * The single-flag selection factory (global mock↔live). Retained for callers/
 * tests that want a uniform client; the active transport below uses the
 * per-surface factory so each surface flips independently.
 */
export function createApplicationFormClient(
  mock: ApplicationFormClient,
  live: ApplicationFormClient,
): ApplicationFormClient {
  return IS_APPLICATION_FORM_MOCK ? mock : live;
}

/**
 * The active Application_Form transport. Each surface's methods are selected
 * mock-vs-live independently by {@link createPerSurfaceApplicationFormClient};
 * flipping a surface mock→live is setting that surface's
 * `VITE_USE_MOCK_APPLICATION_FORM_<SURFACE>` flag to `'false'` — no call-site
 * changes.
 */
export const applicationFormClient: ApplicationFormClient =
  createPerSurfaceApplicationFormClient(
    mockApplicationFormClient,
    appSyncApplicationFormClient,
  );
